import { useState, useEffect, useCallback } from 'react';
import {
  Droplets,
  Plus,
  Search,
  Filter,
  RefreshCw,
  MapPin,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Wrench,
  Gauge,
  Waves,
  Thermometer,
  CloudRain,
  Sun,
  Play,
  Pause,
  Settings,
  BarChart3,
  Eye,
  Edit,
  Trash2,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types d'irrigation
const TYPES_IRRIGATION = {
  goutte_a_goutte: { label: 'Goutte à goutte', icon: '💧', color: 'bg-blue-100 text-blue-800' },
  aspersion: { label: 'Aspersion', icon: '🌧️', color: 'bg-cyan-100 text-cyan-800' },
  micro_aspersion: { label: 'Micro-aspersion', icon: '💦', color: 'bg-sky-100 text-sky-800' },
  pivot: { label: 'Pivot', icon: '🔄', color: 'bg-indigo-100 text-indigo-800' },
  enrouleur: { label: 'Enrouleur', icon: '🎯', color: 'bg-violet-100 text-violet-800' },
  gravitaire: { label: 'Gravitaire', icon: '⬇️', color: 'bg-teal-100 text-teal-800' },
  localisee: { label: 'Localisée', icon: '📍', color: 'bg-emerald-100 text-emerald-800' },
};

// Statuts
const STATUTS = {
  planifie: { label: 'Planifié', color: 'bg-gray-100 text-gray-800' },
  en_cours_pose: { label: 'En cours de pose', color: 'bg-yellow-100 text-yellow-800' },
  operationnel: { label: 'Opérationnel', color: 'bg-green-100 text-green-800' },
  en_maintenance: { label: 'En maintenance', color: 'bg-orange-100 text-orange-800' },
  hiverne: { label: 'Hiverné', color: 'bg-blue-100 text-blue-800' },
  hors_service: { label: 'Hors service', color: 'bg-red-100 text-red-800' },
};

// Sources d'eau
const SOURCES_EAU = [
  { value: 'forage', label: 'Forage' },
  { value: 'reseau', label: 'Réseau AEP' },
  { value: 'bassin', label: 'Bassin de rétention' },
  { value: 'riviere', label: 'Rivière / Cours d\'eau' },
  { value: 'lac', label: 'Lac / Étang' },
  { value: 'puits', label: 'Puits' },
];

export default function IrrigationPage() {
  const [activeTab, setActiveTab] = useState('reseaux');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [reseaux, setReseaux] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [poses, setPoses] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  
  // Dialogs
  const [showNewReseau, setShowNewReseau] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showDetailReseau, setShowDetailReseau] = useState(false);
  const [selectedReseau, setSelectedReseau] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Formulaire nouveau réseau
  const [newReseau, setNewReseau] = useState({
    nom: '',
    parcelle_nom: '',
    type_irrigation: 'goutte_a_goutte',
    surface_couverte_ha: '',
    longueur_totale_m: '',
    debit_nominal_m3h: '',
    pression_bar: '',
    source_eau: 'forage',
    notes: '',
  });

  // Formulaire nouvelle session
  const [newSession, setNewSession] = useState({
    reseau_id: '',
    date_debut: new Date().toISOString().slice(0, 16),
    duree_minutes: 60,
    volume_m3: '',
    notes: '',
  });

  const token = localStorage.getItem('token');

  // Charger les données
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashRes, reseauxRes, sessionsRes, alertesRes, posesRes, maintenancesRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/irrigation/dashboard`, { headers }),
        fetch(`${API_URL}/api/irrigation/reseaux`, { headers }),
        fetch(`${API_URL}/api/irrigation/sessions?limit=20`, { headers }),
        fetch(`${API_URL}/api/irrigation/alertes`, { headers }),
        fetch(`${API_URL}/api/irrigation/poses`, { headers }),
        fetch(`${API_URL}/api/irrigation/maintenances`, { headers }),
      ]);

      if (dashRes.status === 'fulfilled' && dashRes.value.ok) {
        setDashboard(await dashRes.value.json());
      }
      if (reseauxRes.status === 'fulfilled' && reseauxRes.value.ok) {
        const data = await reseauxRes.value.json();
        setReseaux(data.reseaux || []);
      }
      if (sessionsRes.status === 'fulfilled' && sessionsRes.value.ok) {
        const data = await sessionsRes.value.json();
        setSessions(data.sessions || []);
      }
      if (alertesRes.status === 'fulfilled' && alertesRes.value.ok) {
        const data = await alertesRes.value.json();
        setAlertes(data.alertes || []);
      }
      if (posesRes.status === 'fulfilled' && posesRes.value.ok) {
        const data = await posesRes.value.json();
        setPoses(data.poses || []);
      }
      if (maintenancesRes.status === 'fulfilled' && maintenancesRes.value.ok) {
        const data = await maintenancesRes.value.json();
        setMaintenances(data.maintenances || []);
      }
    } catch (error) {
      toast.error('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Créer un réseau
  const handleCreateReseau = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/irrigation/reseaux`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newReseau,
          surface_couverte_ha: parseFloat(newReseau.surface_couverte_ha) || 0,
          longueur_totale_m: parseFloat(newReseau.longueur_totale_m) || null,
          debit_nominal_m3h: parseFloat(newReseau.debit_nominal_m3h) || null,
          pression_bar: parseFloat(newReseau.pression_bar) || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur création');
      }

      toast.success('Réseau créé avec succès');
      setShowNewReseau(false);
      resetReseauForm();
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Créer une session d'irrigation
  const handleCreateSession = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/irrigation/sessions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reseau_id: newSession.reseau_id,
          date_debut: newSession.date_debut,
          duree_minutes: parseInt(newSession.duree_minutes) || 60,
          volume_m3: parseFloat(newSession.volume_m3) || null,
          notes: newSession.notes || null,
          automatique: false,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur création session');
      }

      toast.success('Session d\'irrigation programmée');
      setShowNewSession(false);
      resetSessionForm();
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Résoudre une alerte
  const handleResolveAlerte = async (alerteId) => {
    try {
      const res = await fetch(`${API_URL}/api/irrigation/alertes/${alerteId}/resoudre`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Alerte résolue');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Export des données
  const handleExport = async (format) => {
    try {
      toast.info(`Export ${format.toUpperCase()} en cours...`);
      const res = await fetch(`${API_URL}/api/irrigation/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Erreur export');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `irrigation_export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Changer le statut d'un réseau
  const handleChangeStatut = async (reseauId, newStatut) => {
    try {
      const res = await fetch(`${API_URL}/api/irrigation/reseaux/${reseauId}/statut`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ statut: newStatut }),
      });

      if (res.ok) {
        toast.success('Statut mis à jour');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetReseauForm = () => {
    setNewReseau({
      nom: '',
      parcelle_nom: '',
      type_irrigation: 'goutte_a_goutte',
      surface_couverte_ha: '',
      longueur_totale_m: '',
      debit_nominal_m3h: '',
      pression_bar: '',
      source_eau: 'forage',
      notes: '',
    });
  };

  const resetSessionForm = () => {
    setNewSession({
      reseau_id: '',
      date_debut: new Date().toISOString().slice(0, 16),
      duree_minutes: 60,
      volume_m3: '',
      notes: '',
    });
  };

  const alertesNonResolues = alertes.filter(a => !a.resolu);

  return (
    <div className="p-6 space-y-6" data-testid="irrigation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Droplets className="w-6 h-6 text-blue-600" />
            Gestion de l'Irrigation
          </h1>
          <p className="text-muted-foreground">
            Réseaux, planification, consommation et alertes
          </p>
        </div>
        <div className="flex gap-2">
          <Select onValueChange={(format) => handleExport(format)}>
            <SelectTrigger className="w-[140px]">
              <Download className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Exporter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">Export CSV</SelectItem>
              <SelectItem value="excel">Export Excel</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowNewSession(true)}>
            <Play className="w-4 h-4 mr-2" />
            Nouvelle session
          </Button>
          <Button onClick={() => setShowNewReseau(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau réseau
          </Button>
        </div>
      </div>

      {/* Dashboard KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Waves className="w-4 h-4" />
              <span className="text-xs font-medium">Réseaux</span>
            </div>
            <div className="text-2xl font-bold">{dashboard?.stats?.total_reseaux || reseaux.length}</div>
            <div className="text-xs text-muted-foreground">
              {dashboard?.stats?.reseaux_operationnels || reseaux.filter(r => r.statut === 'operationnel').length} opérationnels
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <MapPin className="w-4 h-4" />
              <span className="text-xs font-medium">Surface irriguée</span>
            </div>
            <div className="text-2xl font-bold">
              {(dashboard?.stats?.surface_irriguee_ha || reseaux.reduce((acc, r) => acc + (r.surface_couverte_ha || 0), 0)).toFixed(1)} ha
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-cyan-600 mb-1">
              <Droplets className="w-4 h-4" />
              <span className="text-xs font-medium">Conso. ce mois</span>
            </div>
            <div className="text-2xl font-bold">{dashboard?.stats?.consommation_mois_m3?.toFixed(0) || 0} m³</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Sessions actives</span>
            </div>
            <div className="text-2xl font-bold">{sessions.filter(s => s.statut === 'en_cours').length}</div>
          </CardContent>
        </Card>
        <Card className={alertesNonResolues.length > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className={`flex items-center gap-2 mb-1 ${alertesNonResolues.length > 0 ? 'text-red-600' : 'text-gray-600'}`}>
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">Alertes</span>
            </div>
            <div className="text-2xl font-bold">{dashboard?.stats?.alertes_actives || alertesNonResolues.length}</div>
            <div className="text-xs text-muted-foreground">à traiter</div>
          </CardContent>
        </Card>
      </div>

      {/* Alertes urgentes */}
      {alertesNonResolues.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-4 h-4" />
              Alertes à traiter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertesNonResolues.slice(0, 3).map((alerte) => (
                <div key={alerte.id} className="flex items-center justify-between p-2 bg-white rounded border">
                  <div>
                    <span className="font-medium">{alerte.message}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      - {alerte.reseau_nom || 'Général'}
                    </span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleResolveAlerte(alerte.id)}>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Résoudre
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="reseaux" className="gap-2">
            <Waves className="w-4 h-4" />
            Réseaux ({reseaux.length})
          </TabsTrigger>
          <TabsTrigger value="sessions" className="gap-2">
            <Play className="w-4 h-4" />
            Sessions ({sessions.length})
          </TabsTrigger>
          <TabsTrigger value="poses" className="gap-2">
            <Wrench className="w-4 h-4" />
            Poses & Travaux ({poses.length})
          </TabsTrigger>
          <TabsTrigger value="maintenances" className="gap-2">
            <Settings className="w-4 h-4" />
            Maintenances ({maintenances.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab Réseaux */}
        <TabsContent value="reseaux">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Réseau</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Parcelle</TableHead>
                    <TableHead className="text-right">Surface</TableHead>
                    <TableHead>Source eau</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : reseaux.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Aucun réseau d'irrigation enregistré
                      </TableCell>
                    </TableRow>
                  ) : (
                    reseaux.map((reseau) => {
                      const type = TYPES_IRRIGATION[reseau.type_irrigation] || TYPES_IRRIGATION.goutte_a_goutte;
                      const statut = STATUTS[reseau.statut] || STATUTS.planifie;
                      
                      return (
                        <TableRow key={reseau.id}>
                          <TableCell className="font-medium">{reseau.nom}</TableCell>
                          <TableCell>
                            <Badge className={type.color}>
                              <span className="mr-1">{type.icon}</span>
                              {type.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{reseau.parcelle_nom || '-'}</TableCell>
                          <TableCell className="text-right">{reseau.surface_couverte_ha} ha</TableCell>
                          <TableCell className="capitalize">{reseau.source_eau?.replace('_', ' ')}</TableCell>
                          <TableCell>
                            <Select
                              value={reseau.statut}
                              onValueChange={(v) => handleChangeStatut(reseau.id, v)}
                            >
                              <SelectTrigger className="w-[140px] h-8">
                                <Badge className={statut.color}>{statut.label}</Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUTS).map(([key, val]) => (
                                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Sessions */}
        <TabsContent value="sessions">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Réseau</TableHead>
                    <TableHead>Date/Heure</TableHead>
                    <TableHead className="text-right">Durée</TableHead>
                    <TableHead className="text-right">Volume</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Aucune session d'irrigation
                      </TableCell>
                    </TableRow>
                  ) : (
                    sessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell className="font-medium">{session.reseau_nom || session.reseau_id}</TableCell>
                        <TableCell>
                          {new Date(session.date_debut).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell className="text-right">{session.duree_minutes} min</TableCell>
                        <TableCell className="text-right">
                          {session.volume_m3 ? `${session.volume_m3} m³` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            session.statut === 'en_cours' ? 'bg-green-100 text-green-800' :
                            session.statut === 'termine' ? 'bg-gray-100 text-gray-800' :
                            'bg-blue-100 text-blue-800'
                          }>
                            {session.statut === 'en_cours' ? 'En cours' :
                             session.statut === 'termine' ? 'Terminée' : 'Planifiée'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {session.statut === 'planifie' && (
                            <Button size="sm" variant="outline" className="text-green-600">
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          {session.statut === 'en_cours' && (
                            <Button size="sm" variant="outline" className="text-red-600">
                              <Pause className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Poses */}
        <TabsContent value="poses">
          <Card>
            <CardContent className="p-6">
              {poses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Wrench className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune pose planifiée</p>
                  <Button variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Planifier une pose
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Réseau</TableHead>
                      <TableHead>Date prévue</TableHead>
                      <TableHead>Équipe</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {poses.map((pose) => (
                      <TableRow key={pose.id}>
                        <TableCell>{pose.reseau_nom}</TableCell>
                        <TableCell>{new Date(pose.date_prevue).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>{pose.equipe || '-'}</TableCell>
                        <TableCell>
                          <Badge>{pose.statut}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Maintenances */}
        <TabsContent value="maintenances">
          <Card>
            <CardContent className="p-6">
              {maintenances.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune maintenance enregistrée</p>
                  <Button variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter une maintenance
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Réseau</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Intervenant</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maintenances.map((maint) => (
                      <TableRow key={maint.id}>
                        <TableCell>{maint.reseau_nom}</TableCell>
                        <TableCell>{maint.type_maintenance}</TableCell>
                        <TableCell>{new Date(maint.date).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>{maint.intervenant || '-'}</TableCell>
                        <TableCell className="max-w-xs truncate">{maint.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nouveau Réseau */}
      <Dialog open={showNewReseau} onOpenChange={setShowNewReseau}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Droplets className="w-5 h-5 text-blue-600" />
              Nouveau Réseau d'Irrigation
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom du réseau *</Label>
              <Input
                value={newReseau.nom}
                onChange={(e) => setNewReseau({ ...newReseau, nom: e.target.value })}
                placeholder="Ex: Goutte à goutte Vignes Nord"
              />
            </div>
            <div className="space-y-2">
              <Label>Parcelle associée</Label>
              <Input
                value={newReseau.parcelle_nom}
                onChange={(e) => setNewReseau({ ...newReseau, parcelle_nom: e.target.value })}
                placeholder="Nom de la parcelle"
              />
            </div>
            <div className="space-y-2">
              <Label>Type d'irrigation *</Label>
              <Select
                value={newReseau.type_irrigation}
                onValueChange={(v) => setNewReseau({ ...newReseau, type_irrigation: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPES_IRRIGATION).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <span className="mr-2">{val.icon}</span>
                      {val.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Source d'eau *</Label>
              <Select
                value={newReseau.source_eau}
                onValueChange={(v) => setNewReseau({ ...newReseau, source_eau: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES_EAU.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Surface couverte (ha) *</Label>
              <Input
                type="number"
                step="0.01"
                value={newReseau.surface_couverte_ha}
                onChange={(e) => setNewReseau({ ...newReseau, surface_couverte_ha: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Longueur totale (m)</Label>
              <Input
                type="number"
                value={newReseau.longueur_totale_m}
                onChange={(e) => setNewReseau({ ...newReseau, longueur_totale_m: e.target.value })}
                placeholder="Mètres linéaires"
              />
            </div>
            <div className="space-y-2">
              <Label>Débit nominal (m³/h)</Label>
              <Input
                type="number"
                step="0.1"
                value={newReseau.debit_nominal_m3h}
                onChange={(e) => setNewReseau({ ...newReseau, debit_nominal_m3h: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Pression (bar)</Label>
              <Input
                type="number"
                step="0.1"
                value={newReseau.pression_bar}
                onChange={(e) => setNewReseau({ ...newReseau, pression_bar: e.target.value })}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newReseau.notes}
                onChange={(e) => setNewReseau({ ...newReseau, notes: e.target.value })}
                placeholder="Informations complémentaires..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewReseau(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateReseau}
              disabled={processing || !newReseau.nom || !newReseau.surface_couverte_ha}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer le réseau
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nouvelle Session */}
      <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-600" />
              Nouvelle Session d'Irrigation
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Réseau *</Label>
              <Select
                value={newSession.reseau_id}
                onValueChange={(v) => setNewSession({ ...newSession, reseau_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un réseau" />
                </SelectTrigger>
                <SelectContent>
                  {reseaux.filter(r => r.statut === 'operationnel').map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date et heure de début *</Label>
              <Input
                type="datetime-local"
                value={newSession.date_debut}
                onChange={(e) => setNewSession({ ...newSession, date_debut: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Durée (min)</Label>
                <Input
                  type="number"
                  value={newSession.duree_minutes}
                  onChange={(e) => setNewSession({ ...newSession, duree_minutes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Volume (m³)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newSession.volume_m3}
                  onChange={(e) => setNewSession({ ...newSession, volume_m3: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newSession.notes}
                onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                placeholder="Remarques..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSession(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateSession}
              disabled={processing || !newSession.reseau_id}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Programmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
